import type { AppDispatch } from '@/store/store';
import type { RootState } from '@/store/rootState';
import type { Services } from '@/infrastructure/services';
import type { Keyframe, SupabaseKeyframe } from '@/modules/keyframes/types';
import { setAssetKeyframes, setAssetKeyframesLoading, upsertKeyframe, removeKeyframe, selectKeyframeById, selectKeyframesByAssetId } from './keyframesSlice';
import { parseVector3String } from '@/utils/numberUtils';
import { changedSince, confirmedAs, lastConfirmed, lastReportedPose, readStarted, wasDeletedHere } from '@/modules/keyframes/localEdits';
import { allowedKeyframeTransform, assetCategoryIn, placedKeyframeTransform } from '@/modules/keyframes/anchorKeyframes';
import { isAnchorCategory } from '@/types/assetTypes';

import type { WriteScope } from '@/modules/undoRedo';
export const loadKeyframesForAsset = (services: Services, assetInstanceId: string) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    if (!assetInstanceId) {
      console.warn('loadKeyframesForAsset called with empty assetInstanceId');
      return;
    }
    
    dispatch(setAssetKeyframesLoading({ assetInstanceId, loading: true }));
    const readAt = readStarted();
    try {
      const raw: SupabaseKeyframe[] = await services.keyframe.fetchKeyframesForAsset(assetInstanceId);
      dispatch(setAssetKeyframes({ assetInstanceId, keyframes: keepLocalEdits(getState(), assetInstanceId, raw.map(toKeyframe), readAt) }));
    } catch (e) {
      dispatch(setAssetKeyframesLoading({ assetInstanceId, loading: false }));
      throw e;
    }
  };

// One request for the whole id set; per-asset fetches fan out as N+1 (lureo-docs#277)
export const loadKeyframesForAssets = (services: Services, assetInstanceIds: string[]) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const ids = assetInstanceIds.filter(Boolean);
    if (ids.length === 0) return;

    ids.forEach((assetInstanceId) => dispatch(setAssetKeyframesLoading({ assetInstanceId, loading: true })));
    const readAt = readStarted();
    try {
      const raw: SupabaseKeyframe[] = await services.keyframe.fetchKeyframesForAssets(ids);
      const byAsset = new Map<string, Keyframe[]>(ids.map((id) => [id, []]));
      raw.forEach((kf) => {
        byAsset.get(kf.asset_instance_id)?.push(toKeyframe(kf));
      });
      byAsset.forEach((keyframes, assetInstanceId) => {
        dispatch(setAssetKeyframes({ assetInstanceId, keyframes: keepLocalEdits(getState(), assetInstanceId, keyframes, readAt) }));
      });
    } catch (e) {
      ids.forEach((assetInstanceId) => dispatch(setAssetKeyframesLoading({ assetInstanceId, loading: false })));
      throw e;
    }
  };

// One the editor has not loaded yet takes the pose the renderer last reported, which may not be saved yet
function keepLocalEdits(state: RootState, assetInstanceId: string, read: Keyframe[], readAt: number): Keyframe[] {
  const kept = read
    .map((kf) => {
      if (!changedSince(kf.id, readAt)) {
        confirmedAs(kf, readAt);
        return kf;
      }
      const held = selectKeyframeById(state, kf.id);
      if (held) return held;
      return wasDeletedHere(kf.id) ? undefined : { ...kf, ...lastReportedPose(kf.id) };
    })
    .filter((kf): kf is Keyframe => Boolean(kf));
  const readIds = new Set(read.map((kf) => kf.id));
  const madeHere = selectKeyframesByAssetId(state, assetInstanceId).filter((kf) => !readIds.has(kf.id) && changedSince(kf.id, readAt));
  return [...kept, ...madeHere];
}

export function toKeyframe(kf: SupabaseKeyframe): Keyframe {
  return {
    id: kf.keyframe_id,
    timePosition: kf.keyframe_time,
    assetInstanceId: kf.asset_instance_id,
    sceneInstanceId: kf.scene_instance_id,
    position: toVec(kf.keyframe_position),
    rotation: toVec(kf.keyframe_rotation),
    scale: toVec(kf.keyframe_scale),
    category: kf.keyframe_category || 'middle',
    createdAt: kf.created_at,
  };
}

export const addKeyframeThunk = (services: Services, kf: Keyframe, scope?: WriteScope) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    const category = assetCategoryIn(getState(), kf.assetInstanceId);
    // An anchor has its start keyframe only, which placing it creates
    if (isAnchorCategory(category) && kf.category !== 'start') return;
    kf = { ...kf, ...placedKeyframeTransform(category, {
      position: kf.position || { x: 0, y: 0, z: 0 },
      rotation: kf.rotation || { x: 0, y: 0, z: 0 },
      scale: kf.scale || { x: 1, y: 1, z: 1 },
    }) };
    dispatch(upsertKeyframe(kf));
    try {
      await services.keyframe.createKeyframe(
        kf.assetInstanceId,
        kf.timePosition,
        kf.position!,
        kf.rotation!,
        kf.scale!,
        kf.category || 'middle',
        kf.sceneInstanceId,
        kf.id,
        scope
      );
    } catch (error) {
      // Never saved, so the keyframe shown for it goes; the failed-write handling tells the user
      dispatch(removeKeyframe({ id: kf.id, assetInstanceId: kf.assetInstanceId }));
      throw error;
    }
    services.analytics.trackLureoKeyframeAdded({
      scene_instance_id: kf.sceneInstanceId,
      asset_instance_id: kf.assetInstanceId,
    });
  };

export const updateKeyframeThunk = (services: Services, kf: Keyframe, scope?: WriteScope) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    // Snapshot prior state from Redux BEFORE we upsert, so the service can diff
    // without an extra DB round-trip (issue #64).
    const previous = selectKeyframeById(getState(), kf.id) ?? null;
    // An anchor's keyframe keeps what it holds wherever the write came from, in the editor as in the save
    const category = assetCategoryIn(getState(), kf.assetInstanceId);
    const allowed = allowedKeyframeTransform(category, previous, kf);
    if (allowed !== kf) {
      kf = { ...kf, position: allowed.position ?? previous?.position, rotation: previous?.rotation, scale: previous?.scale };
      if (sameKeyframe(previous ?? undefined, kf)) return;
    }
    dispatch(upsertKeyframe(kf));
    try {
      await services.keyframe.updateKeyframe(
        kf.id,
        kf.timePosition,
        kf.position,
        kf.rotation,
        kf.scale,
        kf.category,
        previous,
        { scope },
      );
    } catch (error) {
      // Unless a newer edit has replaced it already; the failed-write handling tells the user, once however many keyframes failed
      const saved = lastConfirmed(kf.id) ?? previous;
      if (saved && sameKeyframe(selectKeyframeById(getState(), kf.id), kf)) {
        dispatch(upsertKeyframe(saved));
        // The renderer was already shown the edit (a slider preview or a gizmo move); it goes back to what is saved
        services.keyframe.sendKeyframeToRenderer(saved);
      }
      throw error;
    }
  };

function sameKeyframe(a: Keyframe | undefined, b: Keyframe): boolean {
  const vec = (u?: { x: number; y: number; z: number }, v?: { x: number; y: number; z: number }) =>
    (!u && !v) || (!!u && !!v && u.x === v.x && u.y === v.y && u.z === v.z);
  return !!a && a.timePosition === b.timePosition && vec(a.position, b.position) && vec(a.rotation, b.rotation) && vec(a.scale, b.scale);
}

export const deleteKeyframeThunk = (services: Services, id: string, assetInstanceId: string, scope?: WriteScope) =>
  async (dispatch: AppDispatch, getState: () => RootState): Promise<void> => {
    // An asset is placed by its start keyframe, so that one is never removed on its own
    if (selectKeyframeById(getState(), id)?.category === 'start') return;
    // Only once it is deleted does it leave the editor; the keyframe service tells the renderer
    await services.keyframe.deleteKeyframe(id, assetInstanceId, scope);
    dispatch(removeKeyframe({ id, assetInstanceId }));
  };

function toVec(vectorString?: string): { x: number; y: number; z: number } | undefined {
  if (!vectorString) return undefined;
  return parseVector3String(vectorString) || undefined;
}


